import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiGrid, FiList, FiFileText, FiEdit, FiColumns, FiTrash2, FiEye, FiAlertTriangle } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import { productosService, reportesService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';

const Productos = () => {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Estados para eliminación de productos
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showIncludeInactive, setShowIncludeInactive] = useState(false);
  
  // Aplicar debounce al searchQuery (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Carga inicial y cuando cambia el filtro de inactivos
  useEffect(() => {
    loadProductos();
    // eslint-disable-next-line
  }, [showIncludeInactive]);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    if (debouncedSearchQuery.trim()) {
      performSearch(debouncedSearchQuery);
    } else if (searchQuery === '') {
      // Solo recargar si se limpió la búsqueda
      loadProductos();
    }
    // eslint-disable-next-line
  }, [debouncedSearchQuery]);

  /**
   * Transformar datos de la API al formato de la UI
   */
  const transformProductoFromAPI = useCallback((producto) => ({
    id: producto.id,
    nombreProducto: producto.name || '',
    codigo: producto.code || '',
    codigoBarras: producto.barcode || '',
    categoria: producto.category || '',
    departamento: producto.product_type || '',
    imagen: producto.image_url || null,
    precio: producto.sale_price || 0,
    precioDistribuidor: producto.wholesale_price || 0,
    costo: producto.cost_price || 0,
    stock: producto.current_stock || 0,
    stockMinimo: producto.min_stock || 0,
    stockMaximo: producto.max_stock || 0,
    status: producto.status || 'active'
  }), []);

  const loadProductos = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Llamada real a la API - traer TODOS los productos
      const params = {
        skip: 0,
        limit: 100 // Límite alto para traer todos los productos
      };

      const result = await productosService.getProductos(params);

      if (result.success) {
        // Transformar los datos de la API al formato que espera la UI
        let productosTransformados = (result.data || []).map(transformProductoFromAPI);

        // Filtrar productos DISCONTINUED e INACTIVE si no se quiere mostrarlos
        if (!showIncludeInactive) {
          productosTransformados = productosTransformados.filter(
            p => p.status !== 'DISCONTINUED' && p.status !== 'INACTIVE'
          );
        }

        setProductos(productosTransformados);
      } else {
        setError(result.message || 'Error al cargar productos');
        setProductos([]);
      }
    } catch (error) {
      setError('Error de conexión al cargar productos');
      console.error('Error de conexión al cargar productos:', error);
      setProductos([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Realizar búsqueda real en la API (se ejecuta con debounce)
   */
  const performSearch = async (query) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Parámetros de búsqueda para la API
      const searchParams = {
        skip: 0,
        limit: 1000,
        search: query // Parámetro de búsqueda
      };
      
      const result = await productosService.getProductos(searchParams);
      
      if (result.success) {
        const productosData = (result.data || []).map(transformProductoFromAPI);
        setProductos(productosData);
      } else {
        setError(result.message || 'Error en la búsqueda');
        setProductos([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching productos:', err);
      setProductos([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate('/system/productos/nuevo');
  };

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate
      };

      const result = await reportesService.exportarProductos(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        // Determinar la extensión del archivo
        const extensions = {
          csv: 'csv',
          pdf: 'pdf',
          excel: 'xlsx'
        };

        // Crear blob y descargar
        const blob = new Blob([result.data], {
          type: mimeTypes[format] || 'application/octet-stream'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generar nombre de archivo apropiado
        const fileName = startDate && endDate
          ? `productos_${startDate}_${endDate}.${extensions[format]}`
          : `productos_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Cerrar modal y mostrar mensaje de éxito
        setShowDownloadModal(false);
        alert(`Reporte descargado exitosamente en formato ${format.toUpperCase()}`);
      } else {
        throw new Error(result.message || 'Error al generar el reporte');
      }
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      alert('Error al descargar el reporte. Por favor intente nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Manejar búsqueda (actualiza query, la búsqueda real se hace con debounce)
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Limpiar búsqueda
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Limpiar errores
  const handleClearError = () => {
    setError(null);
    loadProductos();
  };

  // Manejar click en eliminar producto
  const handleDeleteClick = (e, producto) => {
    e.stopPropagation(); // Evitar que se abra el detalle del producto
    setProductToDelete(producto);
    setShowDeleteModal(true);
  };

  // Confirmar eliminación (soft delete)
  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    setIsDeleting(true);
    try {
      // Usar soft delete - cambiar status a DISCONTINUED
      // Asumimos que tiene transacciones por seguridad
      const result = await productosService.softDeleteProducto(productToDelete.id, true);

      if (result.success) {
        setSuccessMessage(`Producto "${productToDelete.nombreProducto}" eliminado correctamente`);
        // Recargar lista de productos
        loadProductos();
        // Auto-ocultar mensaje después de 5 segundos
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(result.message || 'Error al eliminar el producto');
      }
    } catch (err) {
      console.error('Error eliminando producto:', err);
      setError('Error inesperado al eliminar el producto');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
  };

  // Cancelar eliminación
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  // Toggle para mostrar/ocultar productos inactivos
  const handleToggleInactive = () => {
    setShowIncludeInactive(!showIncludeInactive);
  };

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    return `${parseFloat(price).toFixed(2)}`;
  };

  const translateCategory = (category) => {
    const categoryMap = {
      'CAPSULES': 'CÁPSULAS',
      'POWDERS': 'POLVOS',
      'OILS': 'ACEITES',
      'TEAS': 'INFUSIONES',
      'CREAMS': 'CREMAS',
      'LEAVES': 'HOJAS',
      'LIQUIDS': 'LÍQUIDOS',
      'SUPPLEMENTS': 'SUPLEMENTOS',
      'TINCTURES': 'TINTURAS',
      'TABLETS': 'TABLETAS',
      'EXTRACTS': 'EXTRACTOS',
      'HERBS': 'HIERBAS',
      'VITAMINS': 'VITAMINAS',
      'MINERALS': 'MINERALES',
      'SUPPLIES': 'INSUMOS',
      'PACKAGING': 'EMPAQUE',
      'OTHER': 'OTROS'
    };
    return categoryMap[category?.toUpperCase()] || category?.toUpperCase() || '-';
  };

  const getThumbnailType = (departamento) => {
    const tipo = departamento?.toUpperCase();
    switch (tipo) {
      case 'BETEL':
        return 'blue-dark-gradient';
      case 'NATURALANDLAB':
        return 'green-gradient';
      case 'OTHER_BRANDS':
        return 'purple-gradient';
      case 'INGREDIENT':
        return 'orange-gradient';
      case 'PACKAGING_MATERIAL':
        return 'gray';
      default:
        return 'blue-dark-gradient';
    }
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando productos..." />;
  }

  return (
    <div className="productos-page">
      {/* Success message */}
      {successMessage && (
        <div className="alert alert--success" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">PRODUCTOS</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button className="menu-item menu-item--first" onClick={handleAddNew}>
            + AGREGAR PRODUCTO
          </button>
          
          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE DEL PRODUCTO"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          
          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>
          
          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>

          {/* Filtro para mostrar productos inactivos */}
          <label className="menu-item menu-item--checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showIncludeInactive}
              onChange={handleToggleInactive}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '12px' }}>Mostrar inactivos</span>
          </label>
        </div>
      </div>

      {/* Contenido */}
      {productos.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron productos' : 'No hay productos registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery 
              ? `No se encontraron productos que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer producto al sistema'
            }
          </p>
          {searchQuery && (
            <p>
              <button 
                className="btn btn--ghost" 
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <table className="data-table data-table--productos">
            <thead>
              <tr>
                <th>CÓDIGO</th>
                <th>NOMBRE DEL PRODUCTO</th>
                <th>CATEGORÍA</th>
                <th className="text-center">PRECIO</th>
                <th className="text-center">PRECIO DIST.</th>
                <th className="text-center">STOCK</th>
                <th className="text-center">ESTADO</th>
                <th className="text-center">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {productos.map((producto) => (
                <tr
                  key={producto.id}
                  style={{ cursor: 'pointer' }}
                  className={producto.status === 'DISCONTINUED' || producto.status === 'INACTIVE' ? 'row-inactive' : ''}
                >
                  <td onClick={() => navigate(`/system/productos/${producto.id}`)}>{producto.codigo || '-'}</td>
                  <td onClick={() => navigate(`/system/productos/${producto.id}`)}><strong>{producto.nombreProducto}</strong></td>
                  <td onClick={() => navigate(`/system/productos/${producto.id}`)}>{translateCategory(producto.categoria)}</td>
                  <td className="text-center" onClick={() => navigate(`/system/productos/${producto.id}`)}>{formatPrice(producto.precio)}</td>
                  <td className="text-center" onClick={() => navigate(`/system/productos/${producto.id}`)}>{formatPrice(producto.precioDistribuidor)}</td>
                  <td className="text-center">
                    {producto.stock !== undefined ? (
                      <span className={`badge ${producto.stock <= producto.stockMinimo ? 'badge-warning' : 'badge-success'}`}>
                        {producto.stock}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="text-center" onClick={() => navigate(`/system/productos/${producto.id}`)}>
                    <span className={`badge badge-${
                      producto.status === 'ACTIVE' || producto.status === 'active' ? 'success' :
                      producto.status === 'DISCONTINUED' ? 'danger' :
                      producto.status === 'OUT_OF_STOCK' ? 'warning' : 'secondary'
                    }`}>
                      {producto.status === 'ACTIVE' || producto.status === 'active' ? 'Activo' :
                       producto.status === 'INACTIVE' ? 'Inactivo' :
                       producto.status === 'DISCONTINUED' ? 'Descontinuado' :
                       producto.status === 'OUT_OF_STOCK' ? 'Agotado' : producto.status}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="action-buttons" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        className="btn btn--icon btn--ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/system/productos/${producto.id}`);
                        }}
                        title="Ver detalle"
                      >
                        <FiEye size={16} />
                      </button>
                      <button
                        className="btn btn--icon btn--ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/system/productos/${producto.id}/editar`);
                        }}
                        title="Editar"
                      >
                        <FiEdit size={16} />
                      </button>
                      {producto.status !== 'DISCONTINUED' && producto.status !== 'INACTIVE' && (
                        <button
                          className="btn btn--icon btn--ghost btn--danger"
                          onClick={(e) => handleDeleteClick(e, producto)}
                          title="Eliminar"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {productos.map((producto) => (
            <div
              key={producto.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/productos/${producto.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(producto.departamento)}`}>
                    {producto.imagen ? (
                      <img src={producto.imagen} alt={producto.nombreProducto} />
                    ) : (
                      <span className="logo-text">IMAGEN<br/>PRODUCTO</span>
                    )}
                  </div>
                  <div className="content-info">
                    <div className="content-title">{producto.nombreProducto}</div>
                    <div className="content-subtitle">
                      {translateCategory(producto.categoria)}
                    </div>
                    <div className="content-meta-grid">
                      <span className="meta-item">Precio: {formatPrice(producto.precio)}</span>
                      {producto.stock !== undefined && (
                        <span className={`meta-item ${producto.stock <= producto.stockMinimo ? 'warning' : ''}`}>
                          Stock: {producto.stock}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(producto.departamento)}`}>
                    {producto.imagen ? (
                      <img src={producto.imagen} alt={producto.nombreProducto} className="thumbnail-image-small" />
                    ) : (
                      <span className="logo-text-small">IMG</span>
                    )}
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{producto.nombreProducto}</div>
                    <div className="content-subtitle">
                      {translateCategory(producto.categoria)}
                    </div>
                    <div className="content-meta">
                      <span className="meta-item">Precio: {formatPrice(producto.precio)}</span>
                      <span className="meta-item">Código: {producto.codigo}</span>
                      {producto.stock !== undefined && (
                        <span className={`meta-item ${producto.stock <= producto.stockMinimo ? 'warning' : ''}`}>
                          Stock: {producto.stock}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de descarga de reportes */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Productos"
        moduleName="productos"
        isLoading={isDownloading}
      />

      {/* Modal de confirmación para eliminar producto */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Eliminar Producto"
        message={
          productToDelete ? (
            <div>
              <p>¿Está seguro que desea eliminar el producto <strong>"{productToDelete.nombreProducto}"</strong>?</p>
              <div className="alert alert--warning" style={{ marginTop: '12px', padding: '12px' }}>
                <FiAlertTriangle style={{ marginRight: '8px' }} />
                <span>El producto será marcado como <strong>descontinuado</strong> y no aparecerá en el listado principal. Los registros históricos de ventas se mantendrán.</span>
              </div>
            </div>
          ) : 'Confirmar eliminación'
        }
        confirmText={isDeleting ? 'Eliminando...' : 'Sí, eliminar'}
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Productos;
